import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import LocationImage from '../LocationImage';
import { getString, safeRender, createGoogleMapsLink } from './helpers';
import { MapPin, Hotel, ExternalLink, Loader2, BedDouble, Search } from 'lucide-react';

export const LodgingTab = ({
  generatedItinerary,
  currencyMode,
  convertedBudget,
  convertingCurrency,
  toggleCurrency,
  getNationalityCurrency,
  detectBudgetCurrency,
  trip_input,
  handleBookLodging,
}) => {
  return (
    <div className="space-y-6 relative">
      <div className="flex items-center justify-end sticky top-0 z-10 bg-transparent py-1">
        <Button
          variant="default"
          size="sm"
          onClick={toggleCurrency}
          disabled={convertingCurrency}
          className="text-xs bg-ocean hover:bg-ocean-light text-white h-8 px-3"
          data-testid="lodging-currency-toggle"
        >
          {convertingCurrency ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <span className="mr-1">&#8644;</span>
          )}
          {currencyMode === 'destination' ? `Show in ${getNationalityCurrency(trip_input?.nationality)}` : `Show in ${detectBudgetCurrency()}`}
        </Button>
      </div>
      {generatedItinerary.lodging ? (
        Array.isArray(generatedItinerary.lodging) ? (
          generatedItinerary.lodging.slice(0, 5).map((lodge, index) => (
            <Card key={getString(lodge.name || lodge.title || '') || `lodge-${index}`} className="bg-white/95 backdrop-blur-sm border-sky/20 hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="grid md:grid-cols-3 gap-0">
                <div className="md:col-span-1 relative h-48 md:h-full min-h-[12rem] overflow-hidden">
                  <LocationImage
                    query={getString(lodge.name || lodge.title || 'Hotel')}
                    type="hotel"
                    index={index}
                    className="w-full h-full object-cover absolute inset-0"
                    alt={getString(lodge.name || lodge.title || 'Hotel')}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-ocean text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                      #{index + 1} Recommended
                    </span>
                  </div>
                  {lodge.stars && (
                    <div className="absolute top-4 right-4">
                      <span className="bg-gold text-ocean px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                        {'★'.repeat(lodge.stars)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl text-charcoal mb-1">
                        {getString(lodge.name || lodge.title || 'Hotel')}
                      </h3>
                      {lodge.area && (
                        <p className="text-sm text-ocean flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {getString(lodge.area)}
                        </p>
                      )}
                    </div>
                    {lodge.price_range && (
                      <div className="text-right">
                        <p className="text-lg font-bold text-gold">
                          {currencyMode === 'home' && convertedBudget?.rate
                            ? (() => {
                                let result = String(lodge.price_range);
                                // Strip the (approx. ...) parenthetical for home currency
                                result = result.replace(/\s*\(approx\.?[^)]*\)/gi, '');
                                // Strip all currency indicators (symbols with optional prefix, or 3-letter codes before numbers)
                                result = result.replace(/[A-Z]{0,2}[€$£¥₹₫₱₩]\s*/g, '');
                                result = result.replace(/\b[A-Z]{3}\s+(?=\d)/g, '');
                                // Convert all numbers
                                result = result.replace(/(\d[\d,]*(?:\.\d+)?)/g, (m) => {
                                  const n = parseFloat(m.replace(/,/g, ''));
                                  return isNaN(n) ? m : Math.round(n * convertedBudget.rate).toLocaleString();
                                });
                                return (convertedBudget.currency + ' ' + result).replace(/\s+/g, ' ').trim();
                              })()
                            : getString(lodge.price_range)}
                        </p>
                        <p className="text-xs text-charcoal/60">per night</p>
                      </div>
                    )}
                  </div>
                  {lodge.description && (
                    <p className="text-sm text-charcoal/80 leading-relaxed mb-4">{getString(lodge.description)}</p>
                  )}
                  {lodge.amenities && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {Array.isArray(lodge.amenities) ? lodge.amenities.map((amenity, i) => (
                        <span key={`amenity-${getString(amenity).slice(0, 15) || i}`} className="px-3 py-1 bg-sky/10 text-sky text-xs rounded-full font-medium">
                          {getString(amenity)}
                        </span>
                      )) : <span className="text-sm text-charcoal/70">{getString(lodge.amenities)}</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-4">
                    {lodge.website && lodge.website !== 'N/A' && lodge.website !== 'n/a' && (
                      <a href={getString(lodge.website).startsWith('http') ? getString(lodge.website) : `https://${getString(lodge.website)}`} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-ocean hover:bg-ocean-light text-white" size="sm">
                          <ExternalLink className="w-4 h-4 mr-2" /> Hotel Website
                        </Button>
                      </a>
                    )}
                    <a href={createGoogleMapsLink(getString(lodge.google_maps_query || lodge.name || lodge.address || ''))} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-sky text-sky hover:bg-sky/10" size="sm">
                        <MapPin className="w-4 h-4 mr-2" /> Google Maps
                      </Button>
                    </a>
                    <a href={`https://www.expedia.com/Hotel-Search?destination=${encodeURIComponent(getString(lodge.area || lodge.name || trip_input?.destination_city || ''))}&startDate=${trip_input?.start_date || ''}&endDate=${trip_input?.end_date || ''}&adults=${trip_input?.adults || 2}&rooms=1`} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid={`lodge-expedia-${index}`}>
                        <Search className="w-4 h-4 mr-2" /> Expedia
                      </Button>
                    </a>
                    <a href={`https://www.travelean.com`} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-sky hover:bg-sky/90 text-white" size="sm" data-testid={`lodge-travelean-${index}`}>
                        <Search className="w-4 h-4 mr-2" /> Travelean
                      </Button>
                    </a>
                    <a href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(getString(lodge.name || ''))}&checkin=${trip_input?.start_date || ''}&checkout=${trip_input?.end_date || ''}&group_adults=${trip_input?.adults || 2}&no_rooms=1`} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-ocean hover:bg-ocean-light text-white" size="sm" data-testid={`lodge-booking-${index}`}>
                        <Search className="w-4 h-4 mr-2" /> Booking.com
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : typeof generatedItinerary.lodging === 'object' ? (
          Object.entries(generatedItinerary.lodging).map(([cityName, cityLodging]) => (
            <div key={cityName} className="mb-8">
              <h3 className="font-serif text-2xl text-ocean mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" /> {cityName}
              </h3>
              <div className="space-y-4">
                {Array.isArray(cityLodging) ? (
                  cityLodging.slice(0, 5).map((lodge, idx) => (
                    typeof lodge === 'object' && lodge !== null ? (
                      <Card key={getString(lodge.name || lodge.title || '') || `city-lodge-${idx}`} className="bg-white/95 backdrop-blur-sm border-sky/20 hover:shadow-lg transition-all duration-300 overflow-hidden">
                        <div className="grid md:grid-cols-3 gap-0">
                          <div className="md:col-span-1 relative h-48 md:h-full min-h-[12rem] overflow-hidden">
                            <LocationImage
                              query={getString(lodge.name || lodge.title || `${cityName} hotel`)}
                              type="hotel"
                              index={idx}
                              className="w-full h-full object-cover absolute inset-0"
                              alt={getString(lodge.name || lodge.title || 'Hotel')}
                            />
                            <div className="absolute top-4 left-4">
                              <span className="bg-ocean text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                                #{idx + 1} Recommended
                              </span>
                            </div>
                            {lodge.stars && (
                              <div className="absolute top-4 right-4">
                                <span className="bg-gold text-ocean px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                                  {'★'.repeat(lodge.stars)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2 p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="font-serif text-2xl text-charcoal mb-1">{getString(lodge.name || lodge.title || 'Hotel')}</h3>
                                {lodge.area && <p className="text-sm text-ocean flex items-center gap-1"><MapPin className="w-3 h-3" />{getString(lodge.area)}</p>}
                              </div>
                              {lodge.price_range && (
                                <div className="text-right">
                                  <p className="text-lg font-bold text-gold">
                                    {currencyMode === 'home' && convertedBudget?.rate
                                      ? (() => {
                                          let result = String(lodge.price_range);
                                          result = result.replace(/\s*\(approx\.?[^)]*\)/gi, '');
                                          result = result.replace(/[A-Z]{0,2}[€$£¥₹₫₱₩]\s*/g, '');
                                          result = result.replace(/\b[A-Z]{3}\s+(?=\d)/g, '');
                                          result = result.replace(/(\d[\d,]*(?:\.\d+)?)/g, (m) => {
                                            const n = parseFloat(m.replace(/,/g, ''));
                                            return isNaN(n) ? m : Math.round(n * convertedBudget.rate).toLocaleString();
                                          });
                                          return (convertedBudget.currency + ' ' + result).replace(/\s+/g, ' ').trim();
                                        })()
                                      : getString(lodge.price_range)}
                                  </p>
                                  <p className="text-xs text-charcoal/60">per night</p>
                                </div>
                              )}
                            </div>
                            {lodge.description && <p className="text-sm text-charcoal/80 leading-relaxed mb-4">{getString(lodge.description)}</p>}
                            <div className="flex flex-wrap gap-3 mt-4">
                              {lodge.website && lodge.website !== 'N/A' && lodge.website !== 'n/a' && (
                                <a href={getString(lodge.website).startsWith('http') ? getString(lodge.website) : `https://${getString(lodge.website)}`} target="_blank" rel="noopener noreferrer">
                                  <Button className="bg-ocean hover:bg-ocean-light text-white" size="sm"><ExternalLink className="w-4 h-4 mr-2" /> Hotel Website</Button>
                                </a>
                              )}
                              <a href={createGoogleMapsLink(getString(lodge.google_maps_query || lodge.name || ''))} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" className="border-sky text-sky hover:bg-sky/10" size="sm"><MapPin className="w-4 h-4 mr-2" /> Google Maps</Button>
                              </a>
                              <a href={`https://www.expedia.com/Hotel-Search?destination=${encodeURIComponent(cityName)}&startDate=${trip_input?.start_date || ''}&endDate=${trip_input?.end_date || ''}&adults=${trip_input?.adults || 2}&rooms=1`} target="_blank" rel="noopener noreferrer">
                                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm">
                                  <Search className="w-4 h-4 mr-2" /> Expedia
                                </Button>
                              </a>
                              <a href="https://www.travelean.com" target="_blank" rel="noopener noreferrer">
                                <Button className="bg-sky hover:bg-sky/90 text-white" size="sm">
                                  <Search className="w-4 h-4 mr-2" /> Travelean
                                </Button>
                              </a>
                              <a href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(getString(lodge.name || cityName))}&checkin=${trip_input?.start_date || ''}&checkout=${trip_input?.end_date || ''}&group_adults=${trip_input?.adults || 2}&no_rooms=1`} target="_blank" rel="noopener noreferrer">
                                <Button className="bg-ocean hover:bg-ocean-light text-white" size="sm">
                                  <Search className="w-4 h-4 mr-2" /> Booking.com
                                </Button>
                              </a>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ) : (
                      <Card key={`lodge-str-${idx}`} className="bg-white/95 backdrop-blur-sm border-sky/20 p-4">
                        <p className="text-charcoal">{getString(lodge)}</p>
                      </Card>
                    )
                  ))
                ) : typeof cityLodging === 'string' ? (
                  <Card className="bg-white/95 backdrop-blur-sm border-sky/20 p-4">
                    <p className="text-charcoal">{getString(cityLodging)}</p>
                  </Card>
                ) : (
                  <Card className="bg-white/95 backdrop-blur-sm border-sky/20 p-4">
                    <p className="text-charcoal">{safeRender(cityLodging)}</p>
                  </Card>
                )}
              </div>
            </div>
          ))
        ) : (
          <Card className="bg-white/95 backdrop-blur-sm border-sky/20">
            <CardContent className="p-6">
              <p className="text-charcoal">{getString(generatedItinerary.lodging)}</p>
            </CardContent>
          </Card>
        )
      ) : (
        <Card className="bg-white/95 backdrop-blur-sm border-sky/20">
          <CardContent className="p-6">
            <p className="text-charcoal/60">No lodging information available</p>
          </CardContent>
        </Card>
      )}
      
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <a href="https://expedia.com/affiliates/expedia-home.Aim0oVX" target="_blank" rel="noopener noreferrer">
          <Button
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-full font-sans font-semibold transition-all duration-200 hover:shadow-lg active:scale-95"
            data-testid="book-lodging-expedia-btn"
          >
            <BedDouble className="w-5 h-5 mr-2" />
            Search on Expedia
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
        <a href="https://www.travelean.com" target="_blank" rel="noopener noreferrer">
          <Button
            className="bg-gradient-to-r from-sky to-sky/80 text-white px-6 py-3 rounded-full font-sans font-semibold transition-all duration-200 hover:shadow-lg active:scale-95"
            data-testid="book-lodging-travelean-btn"
          >
            <BedDouble className="w-5 h-5 mr-2" />
            Search on Travelean
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>
        <Button
          onClick={handleBookLodging}
          className="bg-ocean hover:bg-ocean-light text-white px-6 py-3 rounded-full font-sans font-semibold transition-all duration-200 hover:shadow-lg active:scale-95"
          data-testid="book-lodging-btn"
        >
          <BedDouble className="w-5 h-5 mr-2" />
          Search on Booking.com
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
